import { h, Fragment } from 'preact';
import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import { useSearchProvider } from '../search/useSearchProvider';
import { FilterPanel } from '../components/filters/FilterPanel';
import { SelectedFilters } from '../components/filters/SelectedFilters';
import { SortDropdown } from '../components/sorting/SortDropdown';
import { MobileFilter } from '../components/filters/MobileFilter';
import { ProductGrid } from '../components/product/ProductGrid';
import { Pagination } from '../components/pagination/Pagination';
import { InfiniteScroll } from '../components/infinite-scroll/InfiniteScroll';
import { ProductCountSkeleton } from '../components/skeleton/ProductCountSkeleton';
import { FilterPanelSkeleton } from '../components/skeleton/FilterPanelSkeleton';
import { SortDropdownSkeleton } from '../components/skeleton/SortDropdownSkeleton';
import { SearchResultsHeaderSkeleton } from '../components/skeleton/SearchResultsHeaderSkeleton';
import { defaultDemoConfig } from '../config/demoConfig';
import { ConfigManager } from '../utils/configManager';
import { ArrowRight } from '../components/icons/arrow-right';
import { Close } from '../components/icons/close';
import { formatProductCount, getRenderedProductCount } from '../utils/productCount';
import AppConfig from '../config/config';
import styles from './Pages.module.scss';
export function BoostPage({ bannerConfig }) {
    // Load config with environment variable priority
    const [config, setConfig] = useState(() => {
        const urlConfig = ConfigManager.getConfigFromUrl();
        if (urlConfig) {
            // URL config takes highest precedence
            return Object.assign(Object.assign({}, defaultDemoConfig), urlConfig);
        }
        // Use the new method that respects env var priority
        return ConfigManager.loadConfigWithEnvPriority();
    });
    // Layout state managed at demo level
    const [layout, setLayout] = useState({
        cardLayout: config.layout.cardLayout,
        filterLayout: config.layout.filterLayout
    });
    // Infinite scroll state
    const [accumulatedProducts, setAccumulatedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    // Track initial mount to prevent duplicate searches
    const isInitialMount = useRef(true);
    // Mobile drawer state
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [initialOpenFilter, setInitialOpenFilter] = useState(undefined);
    // Scroll button state for mobile filter bar
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const filterBarRef = useRef(null);
    // Save config when it changes
    useEffect(() => {
        ConfigManager.saveConfig(config);
    }, [config]);
    const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
    // Handle config change
    const handleConfigChange = (newConfig) => {
        setConfig(newConfig);
        // Re-run search if provider or mock platform changes
        if (newConfig.search.provider !== config.search.provider ||
            (newConfig.search.provider === 'mock' && newConfig.search.mockPlatform !== config.search.mockPlatform)) {
            window.location.reload(); // Reload to reinitialize provider
        }
    };
    const { products, filters, meta, sortOptions, loading, query, currentSort, appliedFilters, handleQuerySubmit: originalHandleQuerySubmit, handleFilterChange: originalHandleFilterChange, handleSortChange: originalHandleSortChange, runSearch } = useSearchProvider(config.search.provider, config.search.mockPlatform);
    // Wrap handlers to reset infinite scroll state
    const handleQuerySubmit = (newQuery) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleQuerySubmit(newQuery);
    };
    const handleFilterChange = (filterId, value) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleFilterChange(filterId, value);
    };
    const handleSortChange = (sortId) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleSortChange(sortId);
    };
    const handleRemoveFilter = (filterId, optionId) => {
        // Toggle the specific filter option off
        handleFilterChange(filterId, optionId);
    };
    const handleClearAllFilters = () => {
        if (appliedFilters === undefined || Object.keys(appliedFilters).length === 0) {
            return; // No filters to clear
        }
        // Clear all selected filters by toggling each one off
        filters.forEach(filter => {
            if (filter.options) {
                filter.options.forEach(option => {
                    if (option.selected) {
                        originalHandleFilterChange(filter.id, option.id);
                    }
                });
            }
        });
        setAccumulatedProducts([]);
        setCurrentPage(1);
    };
    // Mobile drawer handlers
    const openMobileDrawer = (filterId) => {
        setInitialOpenFilter(filterId);
        setMobileDrawerOpen(true);
    };
    const closeMobileDrawer = () => {
        setMobileDrawerOpen(false);
        setInitialOpenFilter(undefined);
    };
    // Handle filter pill click - open drawer with that filter
    const handleFilterPillClick = (filterId) => {
        openMobileDrawer(filterId);
    };
    // Check scroll state for arrow buttons
    const checkScroll = () => {
        if (filterBarRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = filterBarRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };
    // Setup scroll listeners
    useEffect(() => {
        checkScroll();
        const container = filterBarRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            return () => {
                container.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
            };
        }
    }, [filters]);
    // Scroll button handlers
    const scrollLeft = () => {
        if (filterBarRef.current) {
            const filter = filterBarRef.current.querySelector('[class*="filter"]');
            const scrollAmount = filter ? filter.clientWidth + 16 : 200;
            filterBarRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
    };
    const scrollRight = () => {
        if (filterBarRef.current) {
            const filter = filterBarRef.current.querySelector('[class*="filter"]');
            const scrollAmount = filter ? filter.clientWidth + 16 : 200;
            filterBarRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };
    // Initial search is handled by useSearchProvider's URL param initialization
    // No need for a separate initial search effect here
    // Update layout based on config
    useEffect(() => {
        setLayout({
            cardLayout: config.layout.cardLayout,
            filterLayout: config.layout.filterLayout
        });
    }, [config.layout.cardLayout, config.layout.filterLayout]);
    // Reset to first page when pagination type changes
    useEffect(() => {
        // Skip on initial mount - let useSearchProvider handle initial search
        if (isInitialMount.current) {
            return;
        }
        // Reset accumulated products and page when switching pagination types
        setAccumulatedProducts([]);
        setCurrentPage(1);
        // Re-run search to reset results when switching pagination types
        if (runSearch) {
            runSearch(query || '', {
                page: 1,
                pageSize: config.pagination.itemsPerPage,
                sort: currentSort || undefined,
                filters: appliedFilters
            });
        }
    }, [config.pagination.type]);
    // Re-run search when items per page changes
    useEffect(() => {
        // Skip on initial mount - let useSearchProvider handle initial search
        if (isInitialMount.current) {
            isInitialMount.current = false; // Mark as no longer initial mount
            return;
        }
        if (runSearch) {
            // Reset accumulated products when page size changes
            setAccumulatedProducts([]);
            setCurrentPage(1);
            runSearch(query, {
                pageSize: config.pagination.itemsPerPage,
                sort: currentSort || undefined,
                filters: appliedFilters
            });
        }
    }, [config.pagination.itemsPerPage]);
    // Accumulate products for infinite scroll
    useEffect(() => {
        const isInfiniteScroll = config.pagination.type === 'infiniteAuto' || config.pagination.type === 'infiniteButton';
        if (!isInfiniteScroll) {
            return;
        }
        if (currentPage === 1) {
            setAccumulatedProducts(products);
        }
        else {
            // Run only on a response; otherwise load-more appends stale products.
            setAccumulatedProducts(previousProducts => {
                const seenProductIds = new Set(previousProducts.map(product => product.id));
                const newProducts = products.filter(product => !seenProductIds.has(product.id));
                return [...previousProducts, ...newProducts];
            });
        }
        setLoadingMore(false);
    }, [products]);
    // Load more function for infinite scroll
    const loadMoreProducts = () => {
        if (!runSearch || loadingMore || !meta)
            return;
        const nextPage = currentPage + 1;
        if (nextPage > meta.totalPages)
            return;
        setLoadingMore(true);
        setCurrentPage(nextPage);
        runSearch(query || '', {
            page: nextPage,
            pageSize: config.pagination.itemsPerPage,
            sort: currentSort || undefined,
            filters: appliedFilters
        });
    };
    const isInfinitePagination = config.pagination.type === 'infiniteAuto' || config.pagination.type === 'infiniteButton';
    const renderedProducts = useMemo(() => isInfinitePagination
        ? (accumulatedProducts.length > 0 ? accumulatedProducts : products)
        : products, [accumulatedProducts, isInfinitePagination, products]);
    const renderedProductCount = useMemo(() => getRenderedProductCount(renderedProducts, AppConfig.enableColourSwatch), [renderedProducts]);
    // Helper to get product count display
    const getProductCountDisplay = () => {
        return formatProductCount(renderedProductCount);
    };
    // Check if we have no results for a search
    const hasNoResults = !loading && meta && meta.totalResults === 0;
    const hasSearchTerm = query && query.trim().length > 0;
    // Local state for no results search input
    const [noResultsQuery, setNoResultsQuery] = useState(query || '');
    // Update noResultsQuery when query changes
    useEffect(() => {
        setNoResultsQuery(query || '');
    }, [query]);
    // Update search page title when query or results change
    useEffect(() => {
        if (typeof window !== 'undefined' && window.updateSearchPageTitle && query) {
            const resultCount = meta === null || meta === void 0 ? void 0 : meta.totalResults;
            window.updateSearchPageTitle(query, resultCount);
        }
    }, [query, meta === null || meta === void 0 ? void 0 : meta.totalResults]);
    // Set #search-app background to white when no results
    useEffect(() => {
        const searchApp = document.getElementById('search-app');
        if (searchApp) {
            searchApp.style.background = hasNoResults ? '#ffffff' : '';
        }
    }, [hasNoResults]);
    // Render search results header when there are results with a search term
    const renderSearchResultsHeader = () => {
        if (!hasSearchTerm || hasNoResults)
            return null;
        if (loading && !meta) {
            return h(SearchResultsHeaderSkeleton, null);
        }
        return (h("div", { class: styles.searchResultsHeader },
            "Search results for \"",
            query,
            "\""));
    };
    // Render no results UI with search input
    const renderNoResults = () => {
        if (!hasNoResults)
            return null;
        const handleNoResultsSearch = (e) => {
            e.preventDefault();
            if (noResultsQuery.trim()) {
                handleQuerySubmit(noResultsQuery.trim());
            }
        };
        const handleClearInput = () => {
            setNoResultsQuery('');
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (noResultsQuery.trim()) {
                    handleQuerySubmit(noResultsQuery.trim());
                }
            }
        };
        return (h("div", { class: styles.noResultsContainer },
            h("h2", { class: styles.noResultsTitle }, "No results could be found"),
            h("div", { class: styles.noResultsSearchWrapper },
                h("input", { type: "text", class: styles.noResultsSearchInput, value: noResultsQuery, onInput: (e) => setNoResultsQuery(e.currentTarget.value), onKeyDown: handleKeyDown, placeholder: "Search..." }),
                noResultsQuery && (h("button", { type: "button", class: styles.noResultsClearButton, onClick: handleClearInput, "aria-label": "Clear search" },
                    h(Close, null))))));
    };
    // Render main content based on pagination type
    const renderMainContent = () => {
        // Show no results UI if applicable
        if (hasNoResults) {
            return renderNoResults();
        }
        // Force re-render by using key prop
        const contentKey = `${config.pagination.type}-${layout.cardLayout}-${layout.filterLayout}`;
        switch (config.pagination.type) {
            case 'none':
                return h(ProductGrid, { key: contentKey, products: products, loading: loading, layout: layout, bannerConfig: bannerConfig });
            case 'pagination':
                return (h("div", { key: contentKey },
                    h(ProductGrid, { products: products, loading: loading, layout: layout, bannerConfig: bannerConfig }),
                    meta && h(Pagination, { meta: meta, onChange: (page) => {
                            runSearch(query, {
                                page,
                                pageSize: config.pagination.itemsPerPage,
                                sort: currentSort || undefined,
                                filters: appliedFilters
                            });
                        } })));
            case 'infiniteAuto':
                return (h(InfiniteScroll, { key: contentKey, products: accumulatedProducts, loading: loading || loadingMore, layout: layout, mode: "auto", pageSize: config.pagination.itemsPerPage, hasMore: meta ? currentPage < meta.totalPages : false, onLoadMore: loadMoreProducts, bannerConfig: bannerConfig }));
            case 'infiniteButton':
                return (h(InfiniteScroll, { key: contentKey, products: accumulatedProducts, loading: loading || loadingMore, layout: layout, mode: "button", pageSize: config.pagination.itemsPerPage, hasMore: meta ? currentPage < meta.totalPages : false, onLoadMore: loadMoreProducts, bannerConfig: bannerConfig }));
            default:
                return h(ProductGrid, { key: contentKey, products: products, loading: loading, layout: layout, bannerConfig: bannerConfig });
        }
    };
    // Simple layout without Layout component
    const renderSimpleLayout = () => (h("div", { class: "plp-page" },
        config.features.searchBar && !hasNoResults && (h("div", { class: "search-bar" },
            h("input", { type: "text", value: query, onChange: (e) => handleQuerySubmit(e.currentTarget.value), placeholder: "Search products..." }),
            h("button", { onClick: () => handleQuerySubmit(query) }, "Search"))),
        config.features.productCount && !hasNoResults && (loading && !meta ? (h(ProductCountSkeleton, null)) : meta ? (h("div", { class: styles.productCount }, getProductCountDisplay())) : null),
        renderMainContent()));
    // Complex layout with Layout component
    const renderComplexLayout = () => (h("div", { class: "plp-page" },
        h("div", null,
            renderSearchResultsHeader(),
            !hasNoResults && (h(Fragment, null,
                config.features.productCount && (loading && !meta ? (h(ProductCountSkeleton, null)) : meta ? (h("div", { class: styles.productCount, style: "margin-bottom: 16px;" }, getProductCountDisplay())) : null),
                (config.features.filters || config.features.sorting) && (h("div", { class: styles.filtersAndSortWrapper },
                    showLeftArrow && (h("button", { class: `${styles.scrollButton} ${styles.scrollButtonLeft}`, onClick: scrollLeft, "aria-label": "Scroll left" },
                        h("div", { style: "transform: rotate(180deg);" },
                            h(ArrowRight, null)))),
                    h("div", { class: styles.filtersAndSortRow, ref: filterBarRef },
                        config.features.filters && (loading && filters.length === 0 ? (h(FilterPanelSkeleton, { count: 3 })) : (h(FilterPanel, { layout: layout.filterLayout, filters: filters, onFilterChange: handleFilterChange, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer, onMobileClick: openMobileDrawer }))),
                        config.features.sorting && (loading && sortOptions.length === 0 ? (h(SortDropdownSkeleton, null)) : (h(SortDropdown, { options: sortOptions, currentSort: currentSort, onChange: handleSortChange, onMobileClick: openMobileDrawer })))),
                    showRightArrow && (h("button", { class: `${styles.scrollButton} ${styles.scrollButtonRight}`, onClick: scrollRight, "aria-label": "Scroll right" },
                        h(ArrowRight, null))))),
                config.features.filters && (h(SelectedFilters, { filters: filters, onRemoveFilter: handleRemoveFilter, onClearAll: handleClearAllFilters, onPillClick: handleFilterPillClick })))),
            renderMainContent())));
    // Determine which layout to use
    const shouldUseComplexLayout = config.features.filters || config.features.sorting;
    return (h(Fragment, null,
        shouldUseComplexLayout ? renderComplexLayout() : renderSimpleLayout(),
        h(MobileFilter, { isOpen: mobileDrawerOpen, onClose: closeMobileDrawer, filters: filters, onFilterChange: handleFilterChange, sortOptions: sortOptions, currentSort: currentSort, onSortChange: handleSortChange, initialOpenFilter: initialOpenFilter, productCount: renderedProductCount, onClearAll: handleClearAllFilters })));
}
